import { Aide } from '@/components/ui/aide';
import { useSyncOuEnLigne } from '../context/SyncContext';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  CalendarRange,
  Camera,
  ClipboardCheck,
  Plus,
  Receipt,
  Route as RouteIcon,
  Trash2,
} from 'lucide-react';

import { documentsAPI, rentalsAPI, rentalInvoicesAPI, driversAPI, trucksAPI } from '../lib/api';
import { fetchAllPages } from '../lib/fetchAllPages';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { EmptyState } from '../components/ui/empty-state';
import { FieldError } from '../components/ui/field-error';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { LoadingState } from '../components/ui/loading-state';
import { PageHeader } from '../components/ui/page-header';
import { FormActions, FormStep, RecordSplit } from '../components/ui/page-layouts';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Textarea } from '../components/ui/textarea';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { SearchableSelect } from '../components/ui/searchable-select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { toast } from '../components/ui/sonner';
import { driverOption } from '../lib/selectOptions';
import { cn } from '../lib/utils';
import { messageErreur } from '../lib/erreurs';
import { accorde, formatDate, formatMontant, symboleDevise } from '../lib/formats';
import { sommeMontants } from '../lib/montantsFacture';
import { libelle, referenceLisible, texteSur } from '../lib/libelles';
import { PERIOD_UNITS, PRICING_MODES, pricingLabel } from './RentalsPage';
import { useRafraichirSur } from '../hooks/useRafraichirSur';

// Montant en francs CFA, jamais « XOF » à l'écran.
const formatMoney = (amount, currency) =>
  amount === null || amount === undefined || amount === ''
    ? '—'
    : formatMontant(amount, currency || 'XOF');

const CHARGE_TYPES = [
  { value: 'damage', label: 'Dégât' },
  { value: 'missing_fuel', label: 'Carburant manquant' },
  { value: 'late_return', label: 'Retard de restitution' },
  { value: 'fine', label: 'Amende' },
  { value: 'cleaning', label: 'Nettoyage' },
  { value: 'other', label: 'Autre' },
];

// Une checklist d'état des lieux n'est pas un champ libre: ce sont les points
// que le loueur et le locataire signent ensemble, et ils doivent être les mêmes
// au départ et au retour pour que la comparaison ait un sens.
const CHECKLIST_ITEMS = [
  ['tyres', 'Pneumatiques'],
  ['body', 'Carrosserie et cabine'],
  ['tank', 'Citerne et compartiments'],
  ['valves', 'Vannes et flexibles'],
  ['lights', 'Éclairage et signalisation'],
  ['spare', 'Roue de secours et outillage'],
  ['papers', 'Documents de bord'],
  ['extinguisher', 'Extincteur'],
];

const chargeLabel = (value) => libelle('chargeType', value);

const EMPTY_INSPECTION = {
  odometer_km: '',
  fuel_level_liters: '',
  tyres_state: '',
  body_state: '',
  tank_state: '',
  lessee_signature_name: '',
  notes: '',
  actual_return_date: '',
};

const EMPTY_ROTATION = {
  departure_date: '',
  arrival_date: '',
  origin_label: '',
  destination_label: '',
  volume_liters: '',
  distance_km: '',
  rate_amount: '',
  fuel_liters: '',
  note: '',
};

const EMPTY_CHARGE = { charge_type: 'other', label: '', quantity: '1', unit_price: '' };

const RentalContractPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [contract, setContract] = useState(null);
  const [periods, setPeriods] = useState([]);
  const [rotations, setRotations] = useState([]);
  const [inspections, setInspections] = useState([]);
  const [charges, setCharges] = useState([]);
  const [trucks, setTrucks] = useState([]);
  const [drivers, setDrivers] = useState([]);

  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // L'argent attend le serveur: boutons inactifs sans lui.
  const enLigne = useSyncOuEnLigne().isOnline !== false;

  const [showRotation, setShowRotation] = useState(false);
  const [rotationForm, setRotationForm] = useState(EMPTY_ROTATION);
  const [rotationAttempted, setRotationAttempted] = useState(false);

  const [inspectionKind, setInspectionKind] = useState(null); // 'checkout' | 'checkin'
  const [inspectionForm, setInspectionForm] = useState(EMPTY_INSPECTION);
  const [checklist, setChecklist] = useState({});
  const [damages, setDamages] = useState([]);
  // Photos du PV. Un état des lieux sans image se conteste mal: c'est la seule
  // preuve de l'état réel du camion au moment de la remise ou du retour.
  const [inspectionPhotos, setInspectionPhotos] = useState([]);
  const [isUploadingPhoto, setIsUploadingPhoto] = useState(false);
  const [inspectionAttempted, setInspectionAttempted] = useState(false);

  const [showCharge, setShowCharge] = useState(false);
  const [chargeForm, setChargeForm] = useState(EMPTY_CHARGE);

  const [confirmClose, setConfirmClose] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  const load = useCallback(async ({ silencieux = false } = {}) => {
    try {
      if (!silencieux) setIsLoading(true);
      const data = await rentalsAPI.getContract(id);
      setContract(data);
      setPeriods(data.periods || []);
      setRotations(data.rotations || []);
      setInspections(data.inspections || []);
      setCharges(data.charges || []);
      setInvoices(await rentalInvoicesAPI.getForContract(id).catch(() => []));
    } catch (error) {
      console.error('Failed to load rental contract:', error);
      toast.error('Contrat introuvable');
      navigate('/rentals');
    } finally {
      setIsLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    load();
  }, [load]);

  // Une écriture faite ailleurs (autre écran, autre onglet, file hors ligne rejouée) recharge ces chiffres: jamais de compteur figé.
  useRafraichirSur(['locations', 'factures', 'paiements', 'camions'], load);


  useEffect(() => {
    let cancelled = false;
    Promise.all([
      fetchAllPages((page) => trucksAPI.getAllPaged(page)).catch(() => ({ items: [] })),
      fetchAllPages((page) => driversAPI.getAllPaged(page)).catch(() => ({ items: [] })),
    ]).then(([truckPage, driverPage]) => {
      if (cancelled) return;
      setTrucks(truckPage.items || []);
      setDrivers(driverPage.items || []);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const truckMatricule = useMemo(
    () => trucks.find((truck) => truck.id === contract?.truck_id)?.matricule,
    [trucks, contract],
  );
  const camionAffiche = contract?.truck_matricule || truckMatricule || 'Camion non précisé';
  const driverName = useMemo(
    () => drivers.find((driver) => driver.id === contract?.driver_id)?.full_name,
    [drivers, contract],
  );
  const driverOptions = useMemo(
    () => [{ value: 'none', label: 'À désigner' }, ...drivers.map(driverOption)],
    [drivers],
  );

  const checkout = inspections.find((row) => row.kind === 'checkout');
  const checkin = inspections.find((row) => row.kind === 'checkin');

  const rotationsTotal = rotations.reduce(
    (sum, row) => sum + (Number(row.computed_amount) || 0),
    0,
  );
  const periodsTotal = periods.reduce(
    (sum, row) => sum + (Number(row.computed_amount) || 0),
    0,
  );
  const chargesTotal = charges
    .filter((row) => row.status !== 'waived')
    .reduce((sum, row) => sum + (Number(row.amount) || 0), 0);

  // « À facturer » = ce que la prochaine facture reprendrait, avec les filtres
  // du serveur (`rental_invoice_service`): ni brouillon, ni ligne déjà
  // facturée, ni charge abandonnée, et seulement la matière du mode du
  // contrat (périodes OU rotations). L'ancien total additionnait tout.
  const modePeriode = (contract?.pricing_mode || 'period') === 'period';
  const aFacturer = sommeMontants([
    ...(modePeriode
      ? periods.filter((row) => !row.invoice_id && row.status !== 'invoiced')
      : rotations.filter((row) => row.status === 'validated' && !row.invoice_id)
    ).map((row) => row.computed_amount),
    ...charges
      .filter((row) => row.status === 'pending' && !row.invoice_id)
      .map((row) => row.amount),
  ]);

  const isOpen = contract && !['closed', 'cancelled'].includes(contract.status);

  const run = async (action, successMessage) => {
    setIsSubmitting(true);
    try {
      await action();
      if (successMessage) toast.success(successMessage);
      await load();
      return true;
    } catch (error) {
      console.error(error);
      toast.error(messageErreur(error, "L'opération a échoué"));
      return false;
    } finally {
      setIsSubmitting(false);
    }
  };

  const loadPreview = useCallback(async () => {
    setPreviewLoading(true);
    try {
      setPreview(await rentalInvoicesAPI.preview(id));
    } catch (error) {
      setPreview(null);
      toast.error(messageErreur(error, "L'aperçu n'a pas pu être calculé"));
    } finally {
      setPreviewLoading(false);
    }
  }, [id]);

  // La clé survit à un échec réseau et n'est renouvelée qu'après une émission
  // réussie: réessayer après un timeout renvoie la première facture au lieu
  // d'en créer une seconde.
  const idempotencyRef = useRef(null);

  const generateInvoice = async () => {
    if (!idempotencyRef.current) {
      idempotencyRef.current = `${id}-${crypto.randomUUID()}`;
    }
    const ok = await run(
      () => rentalInvoicesAPI.generate(id, { idempotencyKey: idempotencyRef.current }),
      'Facture émise',
    );
    if (ok) {
      idempotencyRef.current = null;
      setPreview(null);
    }
  };

  const openInspection = (kind) => {
    const existing = kind === 'checkout' ? checkout : checkin;
    setInspectionKind(kind);
    setInspectionAttempted(false);
    setChecklist(existing?.checklist_json || {});
    setDamages(existing?.damages_json || []);
    setInspectionPhotos(existing?.photos_json || []);
    setInspectionForm({
      ...EMPTY_INSPECTION,
      odometer_km: existing?.odometer_km ?? '',
      fuel_level_liters: existing?.fuel_level_liters ?? '',
      tyres_state: existing?.tyres_state || '',
      body_state: existing?.body_state || '',
      tank_state: existing?.tank_state || '',
      lessee_signature_name: existing?.lessee_signature_name || '',
      notes: existing?.notes || '',
      actual_return_date: new Date().toISOString().slice(0, 10),
    });
  };

  const submitInspection = async () => {
    setInspectionAttempted(true);
    if (!inspectionForm.lessee_signature_name.trim()) {
      toast.error('La signature du locataire est requise');
      return;
    }
    const toNumber = (value) => (value === '' ? null : Number(value));
    const body = {
      odometer_km: toNumber(inspectionForm.odometer_km),
      fuel_level_liters: toNumber(inspectionForm.fuel_level_liters),
      checklist_json: checklist,
      damages_json: damages,
      photos_json: inspectionPhotos.map((photo) => photo.id),
      tyres_state: inspectionForm.tyres_state || null,
      body_state: inspectionForm.body_state || null,
      tank_state: inspectionForm.tank_state || null,
      lessee_signature_name: inspectionForm.lessee_signature_name.trim(),
      notes: inspectionForm.notes || null,
    };

    const kind = inspectionKind;
    const ok = await run(async () => {
      if (kind === 'checkin') {
        await rentalsAPI.closeWithCheckin(id, {
          ...body,
          actual_return_date: inspectionForm.actual_return_date || null,
        });
      } else {
        await rentalsAPI.recordInspection(id, 'checkout', body);
      }
    }, kind === 'checkin' ? 'Restitution enregistrée, contrat clos' : 'État des lieux de départ enregistré');
    if (ok) setInspectionKind(null);
  };

  const submitRotation = async () => {
    setRotationAttempted(true);
    if (!rotationForm.departure_date) {
      toast.error('La date de départ est requise');
      return;
    }
    if (contract.pricing_mode === 'liter' && !rotationForm.volume_liters) {
      toast.error('Le volume transporté est requis en tarification au litre');
      return;
    }
    const toNumber = (value) => (value === '' ? null : Number(value));
    const ok = await run(
      () =>
        rentalsAPI.createRotation(id, {
          departure_date: rotationForm.departure_date,
          arrival_date: rotationForm.arrival_date || null,
          origin_label: rotationForm.origin_label || null,
          destination_label: rotationForm.destination_label || null,
          volume_liters: toNumber(rotationForm.volume_liters),
          distance_km: toNumber(rotationForm.distance_km),
          rate_amount: toNumber(rotationForm.rate_amount),
          fuel_liters: toNumber(rotationForm.fuel_liters),
          note: rotationForm.note || null,
        }),
      'Rotation enregistrée',
    );
    if (ok) {
      setShowRotation(false);
      setRotationForm(EMPTY_ROTATION);
      setRotationAttempted(false);
    }
  };

  const submitCharge = async () => {
    if (!chargeForm.label.trim()) {
      toast.error('Décrivez la charge');
      return;
    }
    const ok = await run(
      () =>
        rentalsAPI.createCharge(id, {
          charge_type: chargeForm.charge_type,
          label: chargeForm.label.trim(),
          quantity: Number(chargeForm.quantity) || 1,
          unit_price: Number(chargeForm.unit_price) || 0,
        }),
      'Charge ajoutée',
    );
    if (ok) {
      setShowCharge(false);
      setChargeForm(EMPTY_CHARGE);
    }
  };

  const openEdit = () => {
    setEditForm({
      pricing_mode: contract.pricing_mode,
      period_unit: contract.period_unit || 'day',
      rate_amount: contract.rate_amount ?? '',
      included_units: contract.included_units ?? '',
      overage_rate_amount: contract.overage_rate_amount ?? '',
      minimum_billable_units: contract.minimum_billable_units ?? '',
      with_driver: !!contract.with_driver,
      driver_id: contract.driver_id || '',
      fuel_lessee: contract.fuel_responsibility === 'lessee',
      fuel_rate_per_liter: contract.fuel_rate_per_liter ?? '',
      end_date: contract.end_date || '',
      notes: contract.notes || '',
    });
    setEditing(true);
  };

  const submitEdit = async () => {
    const toNumber = (value) => (value === '' ? null : Number(value));
    const ok = await run(
      () =>
        rentalsAPI.updateContract(id, {
          pricing_mode: editForm.pricing_mode,
          period_unit: editForm.pricing_mode === 'period' ? editForm.period_unit : null,
          rate_amount: toNumber(editForm.rate_amount),
          included_units: toNumber(editForm.included_units),
          overage_rate_amount: toNumber(editForm.overage_rate_amount),
          minimum_billable_units: toNumber(editForm.minimum_billable_units),
          with_driver: editForm.with_driver,
          driver_id: editForm.driver_id || null,
          fuel_responsibility: editForm.fuel_lessee ? 'lessee' : 'lessor',
          fuel_rate_per_liter: toNumber(editForm.fuel_rate_per_liter),
          end_date: editForm.end_date || null,
          notes: editForm.notes || null,
        }),
      'Contrat mis à jour',
    );
    if (ok) setEditing(false);
  };

  if (isLoading || !contract) {
    return (
      <div className="mx-auto w-full max-w-page space-y-5 px-4 py-5 md:px-6 md:py-6 lg:px-8">
        <LoadingState shape="detail" />
      </div>
    );
  }

  const identity = (
    <Card className="card-base">
      <CardContent className="space-y-4 p-4">
        <div className="grid grid-cols-2 gap-x-4 gap-y-3 text-sm">
          <Field label="Locataire" value={texteSur(contract.lessee_label, 'Locataire non nommé')} />
          <Field label="Camion" value={camionAffiche} mono />
          <Field label="Tarification" value={pricingLabel(contract)} />
          <Field
            label="Tarif"
            value={formatMoney(contract.rate_amount, contract.currency)}
          />
          <Field
            label="Chauffeur"
            value={
              contract.with_driver
                ? driverName || 'Fourni par le loueur'
                : 'Sans chauffeur — camion hors flotte'
            }
          />
          <Field
            label="Carburant"
            value={
              contract.fuel_responsibility === 'lessee'
                ? 'À la charge du locataire'
                : 'À la charge du loueur'
            }
          />
          <Field label="Début" value={formatDate(contract.start_date)} />
          <Field label="Fin prévue" value={formatDate(contract.end_date, 'Indéterminée')} />
          {contract.actual_return_date && (
            <Field label="Restitué le" value={formatDate(contract.actual_return_date)} />
          )}
          {contract.included_units != null && (
            <Field
              label="Forfait"
              value={`${contract.included_units} unités comprises`}
            />
          )}
          {contract.minimum_billable_units != null && (
            <Field
              label="Plancher"
              value={`${contract.minimum_billable_units} unités`}
            />
          )}
          {contract.deposit_amount != null && (
            <Field
              label="Caution"
              value={formatMoney(contract.deposit_amount, contract.currency)}
            />
          )}
        </div>
        {contract.notes && (
          <p className="border-t border-border pt-3 text-sm text-muted-foreground">
            {texteSur(contract.notes, '')}
          </p>
        )}
      </CardContent>
    </Card>
  );

  const actions = (
    <Card className="card-base">
      <CardContent className="space-y-2 p-4">
        <p className="font-mono text-[11px] uppercase tracking-[0.14em] text-muted-foreground">
          Cycle de vie
        </p>

        {contract.status === 'draft' && (
          <Button
            className="w-full"
            disabled={isSubmitting}
            onClick={() => run(() => rentalsAPI.activateContract(id), 'Contrat activé')}
          >
            Activer le contrat
          </Button>
        )}
        {contract.status === 'active' && (
          <Button
            variant="outline"
            className="w-full"
            disabled={isSubmitting}
            onClick={() => run(() => rentalsAPI.suspendContract(id), 'Contrat suspendu')}
          >
            Suspendre
          </Button>
        )}
        {contract.status === 'suspended' && (
          <Button
            className="w-full"
            disabled={isSubmitting}
            onClick={() => run(() => rentalsAPI.activateContract(id), 'Contrat réactivé')}
          >
            Réactiver
          </Button>
        )}

        <Button
          variant="outline"
          className="w-full"
          disabled={!isOpen}
          onClick={() => openInspection('checkout')}
        >
          {checkout ? "Modifier l'état des lieux de départ" : 'État des lieux de départ'}
        </Button>
        <Button
          variant="outline"
          className="w-full"
          disabled={!isOpen}
          onClick={() => openInspection('checkin')}
        >
          Enregistrer la restitution
        </Button>
        <Button variant="outline" className="w-full" disabled={!isOpen} onClick={openEdit}>
          Modifier le contrat
        </Button>
        <Button
          variant="ghost"
          className="w-full text-destructive hover:text-destructive"
          disabled={!isOpen}
          onClick={() => setConfirmClose(true)}
        >
          Clôturer sans état des lieux
        </Button>
      </CardContent>
    </Card>
  );

  return (
    <div className="mx-auto w-full max-w-page space-y-5 px-4 py-5 md:px-6 md:py-6 lg:px-8">
      <PageHeader
        eyebrow="Location de camion à un client"
        title={referenceLisible(contract.contract_number, 'Contrat de location')}
        backTo="/rentals"
        backLabel="Toutes les locations"
        description={`${texteSur(contract.lessee_label, 'Locataire non nommé')} · ${camionAffiche}`}
        stats={[
          {
            label: 'Loyer par période',
            valeur: periodsTotal,
            unite: symboleDevise(contract.currency),
            aide: `${periods.length} tranche${periods.length > 1 ? 's' : ''}`,
          },
          {
            label: 'Rotations',
            valeur: rotationsTotal,
            unite: symboleDevise(contract.currency),
            aide: `${rotations.length} voyage${rotations.length > 1 ? 's' : ''}`,
          },
          {
            label: 'Charges',
            valeur: chargesTotal,
            unite: symboleDevise(contract.currency),
            aide: 'Dégâts, carburant, retard',
            ton: chargesTotal > 0 ? 'attention' : 'neutre',
          },
          {
            label: 'Total à facturer',
            valeur: aFacturer,
            unite: symboleDevise(contract.currency),
            aide: 'Hors taxe',
          },
        ]}
        actions={
          <Badge className={cn('status-badge', contract.status === 'active' && 'bg-success/15 text-success border-success/40')}>
            {libelle('rentalStatus', contract.status)}
          </Badge>
        }
      />

      <RecordSplit
        main={
          <Tabs defaultValue={contract.pricing_mode === 'period' ? 'periods' : 'rotations'}>
            <TabsList>
              <TabsTrigger value="periods">Périodes</TabsTrigger>
              <TabsTrigger value="rotations">Rotations</TabsTrigger>
              <TabsTrigger value="inspections">État des lieux</TabsTrigger>
              <TabsTrigger value="charges">Charges</TabsTrigger>
              <TabsTrigger value="invoices">Factures</TabsTrigger>
            </TabsList>

            <TabsContent value="periods" className="space-y-3">
              {contract.pricing_mode !== 'period' ? (
                <EmptyState
                  icon={CalendarRange}
                  title="Pas de tranches de loyer"
                  description="Ce contrat est tarifé à la rotation ou au litre : la facturation suit les voyages, pas le calendrier."
                />
              ) : (
                <>
                  <div className="flex justify-end">
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={isSubmitting || !isOpen}
                      onClick={() =>
                        run(() => rentalsAPI.generatePeriods(id), 'Périodes générées')
                      }
                    >
                      Générer les tranches
                    </Button>
                  </div>
                  {periods.length === 0 ? (
                    <EmptyState
                      icon={CalendarRange}
                      title="Aucune tranche générée"
                      description="Générez les tranches pour découper le loyer en lignes facturables."
                    />
                  ) : (
                    <TableShell>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Période</TableHead>
                          <TableHead className="text-right">Unités</TableHead>
                          <TableHead className="text-right">Montant</TableHead>
                          <TableHead>Statut</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {periods.map((period) => (
                          <TableRow key={period.id}>
                            <TableCell className="whitespace-nowrap font-mono text-xs">
                              {formatDate(period.period_start)} → {formatDate(period.period_end)}
                            </TableCell>
                            <TableCell className="text-right tabular-nums">
                              {period.units_count} {period.unit === 'day' ? 'j' : period.unit === 'week' ? 'sem.' : 'mois'}
                            </TableCell>
                            <TableCell className="text-right tabular-nums">
                              {formatMoney(period.computed_amount, contract.currency)}
                            </TableCell>
                            <TableCell className="text-xs text-muted-foreground">
                              {libelle('periodStatus', period.status, 'Ouverte')}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </TableShell>
                  )}
                </>
              )}
            </TabsContent>

            <TabsContent value="rotations" className="space-y-3">
              <div className="flex justify-end">
                <Button
                  size="sm"
                  disabled={!isOpen}
                  onClick={() => setShowRotation(true)}
                  data-testid="new-rotation-btn"
                >
                  <Plus className="h-4 w-4" aria-hidden="true" />
                  Rotation
                </Button>
              </div>
              {rotations.length === 0 ? (
                <EmptyState
                  icon={RouteIcon}
                  title="Aucune rotation"
                  description="Enregistrez chaque voyage effectué sous ce contrat."
                  actionLabel={isOpen ? 'Enregistrer une rotation' : undefined}
                  onAction={() => setShowRotation(true)}
                />
              ) : (
                <TableShell>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Départ</TableHead>
                      <TableHead>Trajet</TableHead>
                      <TableHead className="text-right">Volume</TableHead>
                      <TableHead className="text-right">Montant</TableHead>
                      <TableHead>Statut</TableHead>
                      <TableHead />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {rotations.map((rotation) => (
                      <TableRow key={rotation.id}>
                        <TableCell className="whitespace-nowrap font-mono text-xs">
                          {formatDate(rotation.departure_date)}
                        </TableCell>
                        <TableCell className="max-w-[220px] truncate">
                          {texteSur(rotation.origin_label)} → {texteSur(rotation.destination_label)}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {rotation.volume_liters
                            ? `${new Intl.NumberFormat('fr-FR').format(rotation.volume_liters)} L`
                            : '—'}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {formatMoney(rotation.computed_amount, contract.currency)}
                        </TableCell>
                        <TableCell>
                          {rotation.status === 'draft' ? (
                            <Button
                              size="sm"
                              variant="outline"
                              disabled={isSubmitting}
                              onClick={() =>
                                run(
                                  () => rentalsAPI.validateRotation(rotation.id),
                                  'Rotation validée',
                                )
                              }
                            >
                              Valider
                            </Button>
                          ) : (
                            <span className="text-xs text-muted-foreground">
                              {libelle('rotationStatus', rotation.status, 'Validée')}
                            </span>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          {rotation.status === 'draft' && (
                            <Button
                              size="icon"
                              variant="ghost"
                              className="h-8 w-8 text-destructive hover:text-destructive"
                              aria-label="Supprimer la rotation"
                              onClick={() =>
                                run(
                                  () => rentalsAPI.deleteRotation(rotation.id),
                                  'Rotation supprimée',
                                )
                              }
                            >
                              <Trash2 className="h-4 w-4" aria-hidden="true" />
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </TableShell>
              )}
            </TabsContent>

            <TabsContent value="inspections" className="space-y-3">
              <div className="grid gap-3 md:grid-cols-2">
                <InspectionCard
                  title="Départ"
                  inspection={checkout}
                  onOpen={() => openInspection('checkout')}
                  disabled={!isOpen}
                  currency={contract.currency}
                />
                <InspectionCard
                  title="Retour"
                  inspection={checkin}
                  onOpen={() => openInspection('checkin')}
                  disabled={!isOpen}
                  currency={contract.currency}
                />
              </div>
            </TabsContent>

            <TabsContent value="charges" className="space-y-3">
              <div className="flex justify-end">
                <Button size="sm" disabled={!isOpen || !enLigne} onClick={() => setShowCharge(true)}>
                  <Plus className="h-4 w-4" aria-hidden="true" />
                  Charge
                </Button>
              </div>
              {charges.length === 0 ? (
                <EmptyState
                  icon={Receipt}
                  title="Aucune charge"
                  description="Les dégâts, le carburant manquant et le retard se créent automatiquement à la restitution."
                />
              ) : (
                <TableShell>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Nature</TableHead>
                      <TableHead>Détail</TableHead>
                      <TableHead className="text-right">Montant</TableHead>
                      <TableHead>Statut</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {charges.map((charge) => (
                      <TableRow key={charge.id}>
                        <TableCell className="whitespace-nowrap">
                          {chargeLabel(charge.charge_type)}
                        </TableCell>
                        <TableCell className="max-w-[260px] truncate">
                          {texteSur(charge.label)}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {formatMoney(charge.amount, contract.currency)}
                        </TableCell>
                        <TableCell>
                          {charge.status === 'pending' ? (
                            <Button
                              size="sm"
                              variant="ghost"
                              disabled={isSubmitting || !enLigne}
                              onClick={() =>
                                run(
                                  () =>
                                    rentalsAPI.updateCharge(charge.id, { status: 'waived' }),
                                  'Charge abandonnée',
                                )
                              }
                            >
                              Abandonner
                            </Button>
                          ) : (
                            <span className="text-xs text-muted-foreground">
                              {libelle('chargeStatus', charge.status, 'Abandonnée')}
                            </span>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </TableShell>
              )}
            </TabsContent>

            <TabsContent value="invoices" className="space-y-3">
              <div className="flex flex-wrap items-center justify-end gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  disabled={previewLoading}
                  onClick={loadPreview}
                >
                  {previewLoading ? 'Calcul…' : 'Voir ce qui sera facturé'}
                </Button>
                <Button
                  size="sm"
                  disabled={isSubmitting || !enLigne || !preview || preview.lines?.length === 0}
                  onClick={generateInvoice}
                  data-testid="generate-rental-invoice-btn"
                >
                  <Receipt className="h-4 w-4" aria-hidden="true" />
                  Émettre la facture
                </Button>
              </div>

              {preview && (
                <Card className="card-base">
                  <CardContent className="space-y-3 p-4">
                    <div className="flex items-baseline justify-between gap-3">
                      <div className="flex items-center gap-1">
                        <h3 className="text-sm font-semibold">Aperçu</h3>
                        <Aide titre="Aperçu">Rien n'est écrit tant que la facture n'est pas émise.</Aide>
                      </div>
                    </div>

                    {preview.lines.length === 0 ? (
                      <p className="flex items-center gap-1 text-sm text-muted-foreground">
                        Rien à facturer sur la période.
                        <Aide titre="Rien à facturer">
                          Tout est déjà facturé, ou aucune rotation n'est validée sur la période.
                        </Aide>
                      </p>
                    ) : (
                      <>
                        <TableShell>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Ligne</TableHead>
                              <TableHead className="text-right">Quantité</TableHead>
                              <TableHead className="text-right">Prix unitaire</TableHead>
                              <TableHead className="text-right">Total</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {preview.lines.map((line, index) => (
                              <TableRow key={`${line.kind}-${index}`}>
                                <TableCell className="max-w-[360px]">
                                  <span className="block truncate">{texteSur(line.description, '')}</span>
                                  <span className="text-xs text-muted-foreground">
                                    {libelle('rentalLine', line.kind)}
                                  </span>
                                </TableCell>
                                <TableCell className="text-right tabular-nums">
                                  {line.quantity}
                                  {line.unit_label ? ` ${line.unit_label}` : ''}
                                </TableCell>
                                <TableCell className="text-right tabular-nums">
                                  {formatMoney(line.unit_price, preview.currency)}
                                </TableCell>
                                <TableCell className="text-right tabular-nums">
                                  {formatMoney(line.total, preview.currency)}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </TableShell>

                        <div className="flex items-baseline justify-between border-t border-border pt-3 text-sm">
                          <span className="text-muted-foreground">Net à payer</span>
                          <span className="font-semibold tabular-nums">
                            {formatMoney(preview.total_amount, preview.currency)}
                          </span>
                        </div>
                      </>
                    )}

                    {(preview.warnings || []).map((warning) => (
                      <p
                        key={warning.rotation_id || warning.code}
                        className="text-xs text-warning"
                      >
                        {texteSur(warning.message, '')}
                      </p>
                    ))}
                  </CardContent>
                </Card>
              )}

              {invoices.length === 0 ? (
                <EmptyState
                  icon={Receipt}
                  title="Aucune facture émise"
                  description="Les tranches de loyer, les rotations validées et les charges en attente composent la prochaine facture."
                />
              ) : (
                <TableShell>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Numéro</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead className="text-right">Montant</TableHead>
                      <TableHead>Statut</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {invoices.map((invoice) => (
                      <TableRow key={invoice.id}>
                        <TableCell className="whitespace-nowrap font-mono text-xs">
                          <button
                            type="button"
                            className="underline underline-offset-2 hover:text-primary"
                            onClick={() => navigate(`/invoices?invoice_id=${invoice.id}`)}
                          >
                            {referenceLisible(invoice.invoice_number, 'Facture sans numéro')}
                          </button>
                        </TableCell>
                        <TableCell className="whitespace-nowrap text-xs">
                          {formatDate(invoice.invoice_date)}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {formatMoney(
                            invoice.total_amount,
                            invoice.currency || contract.currency,
                          )}
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground">
                          {libelle('invoiceStatus', invoice.status)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </TableShell>
              )}
            </TabsContent>
          </Tabs>
        }
        aside={
          <>
            {identity}
            {actions}
          </>
        }
      />

      {/* --- État des lieux --- */}
      <Dialog open={!!inspectionKind} onOpenChange={(open) => !open && setInspectionKind(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {inspectionKind === 'checkin'
                ? 'Restitution du camion'
                : 'État des lieux de départ'}
            </DialogTitle>
            <DialogDescription>
              {inspectionKind === 'checkin'
                ? 'Le carburant manquant, les dégâts et le retard deviennent des charges facturables. Le contrat sera clos.'
                : 'Ce constat est la référence opposable au locataire au moment du retour.'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-5">
            <FormStep
              step="1"
              title="Relevés"
              description="Compteur et niveau de carburant au moment du constat."
            >
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label htmlFor="insp-odo">Compteur (km)</Label>
                  <Input
                    id="insp-odo"
                    type="number"
                    min="0"
                    value={inspectionForm.odometer_km}
                    onChange={(event) =>
                      setInspectionForm((prev) => ({
                        ...prev,
                        odometer_km: event.target.value,
                      }))
                    }
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="insp-fuel">Carburant (litres)</Label>
                  <Input
                    id="insp-fuel"
                    type="number"
                    min="0"
                    value={inspectionForm.fuel_level_liters}
                    onChange={(event) =>
                      setInspectionForm((prev) => ({
                        ...prev,
                        fuel_level_liters: event.target.value,
                      }))
                    }
                  />
                </div>
                {inspectionKind === 'checkin' && (
                  <div className="space-y-1.5">
                    <Label htmlFor="insp-return">Date de restitution</Label>
                    <Input
                      id="insp-return"
                      type="date"
                      value={inspectionForm.actual_return_date}
                      onChange={(event) =>
                        setInspectionForm((prev) => ({
                          ...prev,
                          actual_return_date: event.target.value,
                        }))
                      }
                    />
                  </div>
                )}
              </div>
            </FormStep>

            <FormStep
              step="2"
              title="Points à vérifier"
              description="Les mêmes points au départ et au retour, sinon la comparaison ne prouve rien."
            >
              <div className="grid gap-2 sm:grid-cols-2">
                {CHECKLIST_ITEMS.map(([key, label]) => (
                  <div
                    key={key}
                    className="flex items-center justify-between gap-3 rounded-lg border border-border px-3 py-2"
                  >
                    <Label htmlFor={`check-${key}`} className="font-normal">
                      {label}
                    </Label>
                    <Switch
                      id={`check-${key}`}
                      checked={!!checklist[key]}
                      onCheckedChange={(value) =>
                        setChecklist((prev) => ({ ...prev, [key]: value }))
                      }
                    />
                  </div>
                ))}
              </div>
            </FormStep>

            <FormStep
              step="3"
              title="Dégâts constatés"
              description="Un dégât chiffré devient une charge; sans coût estimé, il reste au procès-verbal."
            >
              <div className="space-y-2">
                {damages.map((damage, index) => (
                  <div key={index} className="grid gap-2 sm:grid-cols-[1fr_2fr_auto_auto]">
                    <Input
                      value={damage.zone || ''}
                      placeholder="Zone"
                      onChange={(event) =>
                        setDamages((prev) =>
                          prev.map((item, i) =>
                            i === index ? { ...item, zone: event.target.value } : item,
                          ),
                        )
                      }
                    />
                    <Input
                      value={damage.description || ''}
                      placeholder="Description"
                      onChange={(event) =>
                        setDamages((prev) =>
                          prev.map((item, i) =>
                            i === index
                              ? { ...item, description: event.target.value }
                              : item,
                          ),
                        )
                      }
                    />
                    <Input
                      type="number"
                      min="0"
                      className="w-32"
                      value={damage.estimated_cost ?? ''}
                      placeholder="Coût"
                      onChange={(event) =>
                        setDamages((prev) =>
                          prev.map((item, i) =>
                            i === index
                              ? { ...item, estimated_cost: Number(event.target.value) || 0 }
                              : item,
                          ),
                        )
                      }
                    />
                    <Button
                      type="button"
                      size="icon"
                      variant="ghost"
                      className="h-9 w-9 text-destructive hover:text-destructive"
                      aria-label="Retirer le dégât"
                      onClick={() =>
                        setDamages((prev) => prev.filter((_, i) => i !== index))
                      }
                    >
                      <Trash2 className="h-4 w-4" aria-hidden="true" />
                    </Button>
                  </div>
                ))}
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() =>
                    setDamages((prev) => [
                      ...prev,
                      { zone: '', description: '', estimated_cost: 0 },
                    ])
                  }
                >
                  <Plus className="h-4 w-4" aria-hidden="true" />
                  Ajouter un dégât
                </Button>
              </div>
            </FormStep>

            <FormStep
              step="4"
              title="Photos du constat"
              description="Les images sont archivées dans Documents et restent attachées au procès-verbal."
            >
              <div className="space-y-3">
                <div className="flex flex-wrap items-center gap-2">
                  <Label
                    htmlFor="insp-photos"
                    className="inline-flex cursor-pointer items-center gap-2 rounded-md border border-border px-3 py-2 text-sm hover:bg-muted"
                  >
                    <Camera className="h-4 w-4" aria-hidden="true" />
                    {isUploadingPhoto ? 'Envoi en cours…' : 'Ajouter des photos'}
                  </Label>
                  <input
                    id="insp-photos"
                    type="file"
                    accept="image/*"
                    multiple
                    className="sr-only"
                    disabled={isUploadingPhoto}
                    onChange={async (event) => {
                      const files = Array.from(event.target.files || []);
                      // Le champ est vidé tout de suite: sans cela, choisir
                      // deux fois le même fichier ne déclencherait rien.
                      event.target.value = '';
                      if (!files.length) return;
                      setIsUploadingPhoto(true);
                      try {
                        for (const file of files) {
                          const uploaded = await documentsAPI.upload(file, 'OTHER', {
                            capture_type: 'RENTAL_INSPECTION',
                            capture_source: 'upload',
                          });
                          if (uploaded?.id) {
                            setInspectionPhotos((prev) => [
                              ...prev,
                              { id: uploaded.id, file_name: uploaded.file_name, url: uploaded.url },
                            ]);
                          }
                        }
                      } catch (error) {
                        toast.error(messageErreur(error, "Impossible d'envoyer la photo"));
                      } finally {
                        setIsUploadingPhoto(false);
                      }
                    }}
                  />
                  <span className="text-xs text-muted-foreground">
                    {accorde(inspectionPhotos.length, 'photo jointe', 'photos jointes')}
                  </span>
                </div>

                {inspectionPhotos.length > 0 && (
                  <ul className="space-y-1">
                    {inspectionPhotos.map((photo, index) => (
                      <li
                        key={photo.id || index}
                        className="flex items-center justify-between gap-2 rounded-md border border-border px-3 py-2 text-sm"
                      >
                        <span className="truncate">{texteSur(photo.file_name, 'Photo jointe')}</span>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="text-destructive"
                          onClick={() =>
                            setInspectionPhotos((prev) =>
                              prev.filter((_, i) => i !== index),
                            )
                          }
                        >
                          Retirer
                        </Button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </FormStep>

            <FormStep
              step="5"
              title="Signature"
              description="Le nom du représentant du locataire qui valide ce constat."
            >
              <div className="space-y-1.5">
                <Label htmlFor="insp-signature">Nom du signataire</Label>
                <Input
                  id="insp-signature"
                  value={inspectionForm.lessee_signature_name}
                  onChange={(event) =>
                    setInspectionForm((prev) => ({
                      ...prev,
                      lessee_signature_name: event.target.value,
                    }))
                  }
                />
                <FieldError
                  show={inspectionAttempted && !inspectionForm.lessee_signature_name.trim()}
                >
                  Sans signataire, le constat n'est pas opposable.
                </FieldError>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="insp-notes">Observations</Label>
                <Textarea
                  id="insp-notes"
                  rows={2}
                  value={inspectionForm.notes}
                  onChange={(event) =>
                    setInspectionForm((prev) => ({ ...prev, notes: event.target.value }))
                  }
                />
              </div>
            </FormStep>
          </div>

          <FormActions
            hint={
              inspectionKind === 'checkin'
                ? 'Le contrat passera au statut « clos ».'
                : undefined
            }
          >
            <Button variant="outline" onClick={() => setInspectionKind(null)}>
              Annuler
            </Button>
            <Button onClick={submitInspection} disabled={isSubmitting}>
              {inspectionKind === 'checkin' ? 'Clôturer la location' : 'Enregistrer'}
            </Button>
          </FormActions>
        </DialogContent>
      </Dialog>

      {/* --- Rotation --- */}
      <Dialog open={showRotation} onOpenChange={setShowRotation}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Nouvelle rotation</DialogTitle>
            <DialogDescription>
              {contract.pricing_mode === 'liter'
                ? 'Le montant sera calculé sur le volume transporté.'
                : contract.pricing_mode === 'rotation'
                  ? 'Le forfait du contrat s’applique, sauf tarif saisi ici.'
                  : 'Le loyer ne dépend pas des voyages : cette rotation sert au suivi.'}
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="rot-departure">Date de départ</Label>
              <Input
                id="rot-departure"
                type="date"
                value={rotationForm.departure_date}
                onChange={(event) =>
                  setRotationForm((prev) => ({
                    ...prev,
                    departure_date: event.target.value,
                  }))
                }
              />
              <FieldError show={rotationAttempted && !rotationForm.departure_date}>
                La date de départ est obligatoire.
              </FieldError>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="rot-arrival">Date d'arrivée</Label>
              <Input
                id="rot-arrival"
                type="date"
                value={rotationForm.arrival_date}
                onChange={(event) =>
                  setRotationForm((prev) => ({ ...prev, arrival_date: event.target.value }))
                }
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="rot-origin">Origine</Label>
              <Input
                id="rot-origin"
                value={rotationForm.origin_label}
                onChange={(event) =>
                  setRotationForm((prev) => ({ ...prev, origin_label: event.target.value }))
                }
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="rot-destination">Destination</Label>
              <Input
                id="rot-destination"
                value={rotationForm.destination_label}
                onChange={(event) =>
                  setRotationForm((prev) => ({
                    ...prev,
                    destination_label: event.target.value,
                  }))
                }
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="rot-volume">Volume (litres)</Label>
              <Input
                id="rot-volume"
                type="number"
                min="0"
                value={rotationForm.volume_liters}
                onChange={(event) =>
                  setRotationForm((prev) => ({ ...prev, volume_liters: event.target.value }))
                }
              />
              <FieldError
                show={
                  rotationAttempted &&
                  contract.pricing_mode === 'liter' &&
                  !rotationForm.volume_liters
                }
              >
                Le volume détermine le montant en tarification au litre.
              </FieldError>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="rot-distance">Distance (km)</Label>
              <Input
                id="rot-distance"
                type="number"
                min="0"
                value={rotationForm.distance_km}
                onChange={(event) =>
                  setRotationForm((prev) => ({ ...prev, distance_km: event.target.value }))
                }
              />
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label htmlFor="rot-rate">Tarif exceptionnel</Label>
              <Input
                id="rot-rate"
                type="number"
                min="0"
                placeholder="Laisser vide pour appliquer le tarif du contrat"
                value={rotationForm.rate_amount}
                onChange={(event) =>
                  setRotationForm((prev) => ({ ...prev, rate_amount: event.target.value }))
                }
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRotation(false)}>
              Annuler
            </Button>
            <Button onClick={submitRotation} disabled={isSubmitting}>
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* --- Charge manuelle --- */}
      <Dialog open={showCharge} onOpenChange={setShowCharge}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Ajouter une charge</DialogTitle>
            <DialogDescription>
              Un extra à facturer en plus du loyer.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label htmlFor="charge-type">Nature</Label>
              <Select
                value={chargeForm.charge_type}
                onValueChange={(value) =>
                  setChargeForm((prev) => ({ ...prev, charge_type: value }))
                }
              >
                <SelectTrigger id="charge-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CHARGE_TYPES.map((item) => (
                    <SelectItem key={item.value} value={item.value}>
                      {item.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="charge-label">Détail</Label>
              <Input
                id="charge-label"
                value={chargeForm.label}
                onChange={(event) =>
                  setChargeForm((prev) => ({ ...prev, label: event.target.value }))
                }
              />
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="charge-qty">Quantité</Label>
                <Input
                  id="charge-qty"
                  type="number"
                  min="0"
                  value={chargeForm.quantity}
                  onChange={(event) =>
                    setChargeForm((prev) => ({ ...prev, quantity: event.target.value }))
                  }
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="charge-price">Prix unitaire</Label>
                <Input
                  id="charge-price"
                  type="number"
                  min="0"
                  value={chargeForm.unit_price}
                  onChange={(event) =>
                    setChargeForm((prev) => ({ ...prev, unit_price: event.target.value }))
                  }
                />
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCharge(false)}>
              Annuler
            </Button>
            <Button onClick={submitCharge} disabled={isSubmitting || !enLigne}>
              Ajouter
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* --- Modification du contrat --- */}
      <Dialog open={editing} onOpenChange={setEditing}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Modifier le contrat</DialogTitle>
            <DialogDescription>
              Le mode de tarification décide des champs affichés.
            </DialogDescription>
          </DialogHeader>

          {editForm && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Mode de tarification</Label>
                <div className="grid gap-2 sm:grid-cols-3">
                  {PRICING_MODES.map((mode) => (
                    <button
                      key={mode.value}
                      type="button"
                      aria-pressed={editForm.pricing_mode === mode.value}
                      onClick={() =>
                        setEditForm((prev) => ({ ...prev, pricing_mode: mode.value }))
                      }
                      className={cn(
                        'rounded-lg border p-3 text-left transition-colors',
                        editForm.pricing_mode === mode.value
                          ? 'border-primary bg-primary/5'
                          : 'border-border hover:bg-muted/50',
                      )}
                    >
                      <span className="block text-sm font-medium">{mode.label}</span>
                      <span className="block text-xs text-muted-foreground">{mode.hint}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div className="grid gap-3 sm:grid-cols-2">
                {editForm.pricing_mode === 'period' && (
                  <div className="space-y-1.5">
                    <Label htmlFor="edit-unit">Unité de période</Label>
                    <Select
                      value={editForm.period_unit}
                      onValueChange={(value) =>
                        setEditForm((prev) => ({ ...prev, period_unit: value }))
                      }
                    >
                      <SelectTrigger id="edit-unit">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {PERIOD_UNITS.map((unit) => (
                          <SelectItem key={unit.value} value={unit.value}>
                            {unit.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <div className="space-y-1.5">
                  <Label htmlFor="edit-rate">Tarif</Label>
                  <Input
                    id="edit-rate"
                    type="number"
                    min="0"
                    value={editForm.rate_amount}
                    onChange={(event) =>
                      setEditForm((prev) => ({ ...prev, rate_amount: event.target.value }))
                    }
                  />
                </div>
              </div>

              {editForm.pricing_mode !== 'period' && (
                <div className="grid gap-3 sm:grid-cols-3">
                  <div className="space-y-1.5">
                    <Label htmlFor="edit-included">Unités comprises</Label>
                    <Input
                      id="edit-included"
                      type="number"
                      min="0"
                      value={editForm.included_units}
                      onChange={(event) =>
                        setEditForm((prev) => ({
                          ...prev,
                          included_units: event.target.value,
                        }))
                      }
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="edit-overage">Dépassement</Label>
                    <Input
                      id="edit-overage"
                      type="number"
                      min="0"
                      value={editForm.overage_rate_amount}
                      onChange={(event) =>
                        setEditForm((prev) => ({
                          ...prev,
                          overage_rate_amount: event.target.value,
                        }))
                      }
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="edit-minimum">Plancher</Label>
                    <Input
                      id="edit-minimum"
                      type="number"
                      min="0"
                      value={editForm.minimum_billable_units}
                      onChange={(event) =>
                        setEditForm((prev) => ({
                          ...prev,
                          minimum_billable_units: event.target.value,
                        }))
                      }
                    />
                  </div>
                </div>
              )}

              <div className="space-y-3 rounded-lg border border-border p-3">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div className="flex items-center gap-1">
                      <Label htmlFor="edit-driver-switch">Location avec chauffeur</Label>
                      <Aide titre="Location avec chauffeur">
                        Sans chauffeur, le camion sort de la flotte exploitable.
                      </Aide>
                    </div>
                  </div>
                  <Switch
                    id="edit-driver-switch"
                    checked={editForm.with_driver}
                    onCheckedChange={(checked) =>
                      setEditForm((prev) => ({ ...prev, with_driver: checked }))
                    }
                  />
                </div>
                {editForm.with_driver && (
                  <div className="space-y-1.5">
                    <Label htmlFor="edit-driver">Chauffeur affecté</Label>
                    <SearchableSelect
                      id="edit-driver"
                      placeholder="Choisir"
                      searchPlaceholder="Rechercher un chauffeur…"
                      emptyMessage="Aucun chauffeur trouvé"
                      value={editForm.driver_id || 'none'}
                      onValueChange={(value) =>
                        setEditForm((prev) => ({
                          ...prev,
                          driver_id: value === 'none' ? '' : value,
                        }))
                      }
                      options={driverOptions}
                    />
                  </div>
                )}
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <Label htmlFor="edit-fuel-switch">Carburant à la charge du locataire</Label>
                    <p className="text-xs text-muted-foreground">
                      Le manquant au retour sera refacturé.
                    </p>
                  </div>
                  <Switch
                    id="edit-fuel-switch"
                    checked={editForm.fuel_lessee}
                    onCheckedChange={(checked) =>
                      setEditForm((prev) => ({ ...prev, fuel_lessee: checked }))
                    }
                  />
                </div>
                {editForm.fuel_lessee && (
                  <div className="space-y-1.5">
                    <Label htmlFor="edit-fuel-rate">Prix du litre refacturé</Label>
                    <Input
                      id="edit-fuel-rate"
                      type="number"
                      min="0"
                      value={editForm.fuel_rate_per_liter}
                      onChange={(event) =>
                        setEditForm((prev) => ({
                          ...prev,
                          fuel_rate_per_liter: event.target.value,
                        }))
                      }
                    />
                  </div>
                )}
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="edit-end">Fin prévue</Label>
                <Input
                  id="edit-end"
                  type="date"
                  value={editForm.end_date}
                  onChange={(event) =>
                    setEditForm((prev) => ({ ...prev, end_date: event.target.value }))
                  }
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="edit-notes">Notes</Label>
                <Textarea
                  id="edit-notes"
                  rows={2}
                  value={editForm.notes}
                  onChange={(event) =>
                    setEditForm((prev) => ({ ...prev, notes: event.target.value }))
                  }
                />
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(false)}>
              Annuler
            </Button>
            <Button onClick={submitEdit} disabled={isSubmitting}>
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={confirmClose} onOpenChange={setConfirmClose}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Clôturer sans état des lieux ?</AlertDialogTitle>
            <AlertDialogDescription>
              Aucune charge de restitution ne sera calculée : ni carburant manquant, ni
              dégâts, ni retard. Le camion redevient disponible immédiatement.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => run(() => rentalsAPI.closeContract(id), 'Contrat clos')}
            >
              Clôturer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

const Field = ({ label, value, mono }) => (
  <div className="min-w-0">
    <p className="font-mono text-[10px] uppercase tracking-[0.12em] text-muted-foreground">
      {label}
    </p>
    <p className={cn('truncate text-sm', mono && 'font-mono text-xs')}>{value || '—'}</p>
  </div>
);

const TableShell = ({ children }) => (
  <div className="overflow-hidden rounded-xl border border-border bg-card">
    <Table stickyFirstColumn>{children}</Table>
  </div>
);

const InspectionCard = ({ title, inspection, onOpen, disabled, currency }) => (
  <Card className="card-base">
    <CardContent className="space-y-3 p-4">
      <div className="flex items-center justify-between gap-3">
        <h3 className="text-sm font-semibold">{title}</h3>
        {inspection ? (
          <Badge className="status-badge bg-success/15 text-success border-success/40 text-xs">
            Constaté
          </Badge>
        ) : (
          <Badge className="status-badge border-border bg-muted text-xs text-muted-foreground">
            À faire
          </Badge>
        )}
      </div>

      {inspection ? (
        <div className="space-y-1 text-sm">
          <p className="text-muted-foreground">
            {formatDate(inspection.inspected_at, '')}
          </p>
          <p>Compteur : {inspection.odometer_km ?? '—'} km</p>
          <p>Carburant : {inspection.fuel_level_liters ?? '—'} L</p>
          <p>
            Dégâts : {(inspection.damages_json || []).length} ·{' '}
            {formatMoney(inspection.estimated_repair_cost, currency)}
          </p>
          <p className="text-muted-foreground">
            Signé par {texteSur(inspection.lessee_signature_name)}
          </p>
        </div>
      ) : (
        <p className="text-sm text-muted-foreground">
          <ClipboardCheck className="mr-1 inline h-4 w-4" aria-hidden="true" />
          Aucun constat enregistré.
        </p>
      )}

      <Button variant="outline" size="sm" className="w-full" disabled={disabled} onClick={onOpen}>
        {inspection ? 'Modifier' : 'Renseigner'}
      </Button>
    </CardContent>
  </Card>
);

export default RentalContractPage;
